import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Card, CardBody, CardFooter, CardHeader, Col, Row, Form } from "react-bootstrap";
import { FormikProps, Formik, Field } from "formik";
import { Button } from "primereact/button";
import { request } from "../../../utils/Request";
import Auth from "../../../utils/Auth";
import { InputCheckboxField } from "../../../components/Shared/InputCheckboxField";
import { get } from "lodash";

const APP_SETTING_HIDE_ANNOUNCED_ON_LOADS_SCREEN = "hide_announced_outbound_loads_on_loads_screen";

type TInputForm = {
    hide_announced_outbound_loads_on_loads_screen: boolean,
};

interface IProps {
    t?: any,
    user?: any,
    toastify?: any,
    onUpdated?: any,
}

function LoadScreenSettingProfile(props: IProps) {
    const { t, toastify } = props;
    const auth = new Auth();
    const [initFormState, setInitFormState] = useState<TInputForm>({
        hide_announced_outbound_loads_on_loads_screen: false,
    });

    useEffect(() => {
        setInitFormState({
            hide_announced_outbound_loads_on_loads_screen: Boolean(
                get(props.user, ["app_settings", APP_SETTING_HIDE_ANNOUNCED_ON_LOADS_SCREEN], false)
            ),
        });
    }, [props.user]);

    const onSubmit = async (payloads: TInputForm, { setSubmitting }: any) => {
        try {
            const currentUser: any = auth.getUser();
            const existingAppSettings = get(currentUser, ["app_settings"], {}) || {};

            const nextAppSettings = {
                ...existingAppSettings,
                [APP_SETTING_HIDE_ANNOUNCED_ON_LOADS_SCREEN]: Boolean(payloads.hide_announced_outbound_loads_on_loads_screen),
            };

            const response = await request({
                method: "patch",
                url: "auth/app/settings",
                data: { app_settings: nextAppSettings, _method: "post" },
            });

            const { data } = response.data;
            const updatedSettings = data?.app_settings ?? nextAppSettings;

            currentUser.app_settings = updatedSettings;
            auth.setUser(currentUser);

            toastify?.(t("record updated", { item: t("settings") }), "success");
            props.onUpdated?.(currentUser);

            // This setting affects list rendering on the Loads screen.
            // Reloading is a simple and reliable way to re-fetch with new rules.
            setTimeout(() => window.location.reload(), 700);
        } catch (e: any) {
            toastify?.(t("server error"), "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            enableReinitialize={ true }
            initialValues={ initFormState }
            onSubmit={ onSubmit }
        >
            { (form: FormikProps<TInputForm>) => {
                return (
                    <Form onSubmit={ form.handleSubmit } className={ "mt-3" }>
                        <Card className={ "mb-3" }>
                            <CardHeader className={ "bg-body-tertiary" }>
                                <h4 className="card-title">{ t("loads") } { t("settings") }</h4>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col sm={ 12 }>
                                        <Field
                                            component={ InputCheckboxField }
                                            name="hide_announced_outbound_loads_on_loads_screen"
                                            label={ t("Hide announced outbound loads on Loads screen") }
                                            inline={ true }
                                            disabled={ form.isSubmitting }
                                        />
                                        <small className={ "text-muted d-block mt-1" }>
                                            { t("Only affects the Loads screen. Order Transport Requests is not changed.") }
                                        </small>
                                    </Col>
                                </Row>
                            </CardBody>
                            <CardFooter>
                                <Button
                                    type="submit"
                                    size={ "small" }
                                    disabled={ form.isSubmitting }
                                    className="btn btn-success waves-effect btn-label waves-light mt-2"
                                >
                                    <i className="fas fa-save label-icon me-2" /> { t("let's save") }
                                </Button>
                            </CardFooter>
                        </Card>
                    </Form>
                );
            } }
        </Formik>
    );
}

LoadScreenSettingProfile.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(LoadScreenSettingProfile);

